/**
 * Tickets Print View Component
 *
 * Displays tickets in a printable format with QR codes.
 * Layout: 4 tickets per page with proper page breaks.
 */

import { useEffect } from 'react';
import { MealTicket } from '../hooks/useTickets';

interface TicketsPrintViewProps {
  tickets: MealTicket[];
  onClose: () => void;
}

export function TicketsPrintView({ tickets, onClose }: TicketsPrintViewProps) {
  useEffect(() => {
    // Disable body scrolling when print view is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No Expiration';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      {/* Screen-only controls */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-40 print:hidden">
        <div className="fixed top-0 left-0 right-0 bg-white shadow-lg p-4 z-50 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            Print Preview - {tickets.length} Ticket{tickets.length !== 1 ? 's' : ''}
          </h2>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="px-6 py-2 rounded-lg font-medium text-white transition-colors"
              style={{ backgroundColor: '#10348e' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1e62ac')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#10348e')}
            >
              Print
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>

        {/* Scrollable preview area */}
        <div className="pt-20 pb-4 h-full overflow-auto">
          <div className="max-w-[8.5in] mx-auto bg-white shadow-2xl">
            <PrintableTickets tickets={tickets} formatDate={formatDate} />
          </div>
        </div>
      </div>

      {/* Print-only content */}
      <div className="hidden print:block">
        <PrintableTickets tickets={tickets} formatDate={formatDate} />
      </div>
    </>
  );
}

interface PrintableTicketsProps {
  tickets: MealTicket[];
  formatDate: (date: string | null) => string;
}

function PrintableTickets({ tickets, formatDate }: PrintableTicketsProps) {
  // Group tickets into pages of 4
  const pages: MealTicket[][] = [];
  for (let i = 0; i < tickets.length; i += 4) {
    pages.push(tickets.slice(i, i + 4));
  }

  return (
    <>
      {pages.map((pageTickets, pageIndex) => (
        <div
          key={pageIndex}
          className="relative"
          style={{
            width: '8.5in',
            minHeight: '11in',
            padding: '0.5in',
            pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'auto',
          }}
        >
          {/* Page header */}
          <div className="text-center mb-6 pb-4 border-b-2 border-gray-300">
            <h1
              className="text-2xl font-bold mb-1"
              style={{ color: '#10348e' }}
            >
              HOOD MEMORIAL HOSPITAL
            </h1>
            <h2 className="text-xl font-semibold text-gray-700">Guest Meal Tickets</h2>
          </div>

          {/* Grid of 4 tickets (2x2) */}
          <div className="grid grid-cols-2 gap-4">
            {pageTickets.map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} formatDate={formatDate} />
            ))}
          </div>

          {/* Page footer */}
          <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-500">
            Page {pageIndex + 1} of {pages.length}
          </div>
        </div>
      ))}
    </>
  );
}

interface TicketCardProps {
  ticket: MealTicket;
  formatDate: (date: string | null) => string;
}

function TicketCard({ ticket, formatDate }: TicketCardProps) {
  return (
    <div className="border-2 border-gray-300 rounded-lg p-4 bg-white flex flex-col items-center">
      {/* Ticket header */}
      <div className="text-center mb-3 w-full">
        <div
          className="text-xs font-bold mb-1"
          style={{ color: '#10348e' }}
        >
          GUEST MEAL TICKET
        </div>
        <div className="text-xs text-gray-600 capitalize">
          {ticket.meal_type === 'any' ? 'Any Meal' : ticket.meal_type}
        </div>
      </div>

      {/* QR Code */}
      {ticket.qr_code ? (
        <div className="mb-3 bg-white p-2 border border-gray-200 rounded">
          <img
            src={`data:image/png;base64,${ticket.qr_code}`}
            alt={`QR Code for ticket ${ticket.ticket_code}`}
            className="w-32 h-32"
            style={{ imageRendering: 'pixelated' }}
          />
        </div>
      ) : (
        <div className="mb-3 w-32 h-32 bg-gray-100 flex items-center justify-center border border-gray-300 rounded">
          <span className="text-xs text-gray-500">No QR Code</span>
        </div>
      )}

      {/* Ticket code */}
      <div className="text-center mb-3 w-full">
        <div className="text-xs text-gray-500 mb-1">Ticket Code</div>
        <div className="font-mono font-bold text-lg tracking-wider">
          {ticket.ticket_code}
        </div>
      </div>

      {/* Ticket details */}
      <div className="text-center text-xs text-gray-600 space-y-1 w-full border-t pt-2">
        <div>
          <span className="font-semibold">Expires:</span> {formatDate(ticket.expires_at)}
        </div>
        {ticket.batch_description && (
          <div className="text-gray-500 italic truncate">
            {ticket.batch_description}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-2 text-center text-xs text-gray-500 border-t pt-2 w-full">
        Present this ticket at the cafeteria
      </div>
    </div>
  );
}
